import React, { useMemo } from 'react'
import { Gem, User } from 'lucide-react'
import useCrashStore from '../store/useCrashStore'
import { hundredsToX } from '../utils/crashHelpers'
import ProfileLink from '../../../components/ProfileLink'
import TeamTag from '../../../components/TeamTag'

/**
 * Список игроков текущего раунда (§9).
 *
 * Источник — Map<chatId, player> в store, обновляется идемпотентно
 * из crash:bet / crash:bets / crash:cashout / crash:cashouts / crash:win-skin / crash:players.
 *
 * У каждого игрока показываем:
 * - аватар, имя;
 * - состав ставки: скины (картинки + цены) и кристаллы;
 * - у выведшихся — множитель вывода и выигрыш;
 * - выигранный скин (если был win-skin).
 *
 * Свою строку отдельно рисует родитель (CrashMyBet) — здесь только список всех.
 */
const CrashPlayersList = ({ max = 50 }) => {
  const playersMap = useCrashStore((s) => s.players)
  const myChatId = useCrashStore((s) => s.myChatId)

  // Map → отсортированный массив: своя ставка всегда первая,
  // затем ещё играющие, выведшиеся — внизу; внутри групп —
  // от крупной ставки к мелкой.
  const players = useMemo(() => {
    const arr = Array.from(playersMap.values())
    return arr
      .sort((a, b) => {
        const aMe = myChatId && String(a.chatId) === String(myChatId) ? 1 : 0
        const bMe = myChatId && String(b.chatId) === String(myChatId) ? 1 : 0
        if (aMe !== bMe) return bMe - aMe
        const aOut = a.cashed ? 1 : 0
        const bOut = b.cashed ? 1 : 0
        if (aOut !== bOut) return aOut - bOut
        return Number(b.amount || 0) - Number(a.amount || 0)
      })
      .slice(0, max)
  }, [playersMap, myChatId, max])

  if (players.length === 0) {
    return (
      <div className="text-center py-8 text-text-secondary text-sm">
        {/* Пустой список до первой ставки */}
      </div>
    )
  }

  return (
    <div className="space-y-1.5">
      {players.map((p) => {
        const isMe = myChatId && String(p.chatId) === String(myChatId)
        return <PlayerRow key={p.chatId} player={p} isMe={isMe} />
      })}
    </div>
  )
}

const PlayerRow = ({ player, isMe }) => {
  const { name, ava, slug, team, amount, cashed, cashoutMultiplier, win, winSkin } = player

  return (
    <div
      className={`
        flex items-center gap-2.5 p-2.5 rounded-xl border
        ${isMe
          ? 'bg-gradient-to-r from-accent-primary/15 to-accent-primary/5 border-accent-primary/40 shadow-[inset_0_1px_0_rgba(255,255,255,0.1),0_0_24px_-6px_rgba(0,255,127,0.35)]'
          : 'bg-dark-primary/60 border-white/5 shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]'}
      `}
    >
      {/* Аватар — кликабелен при slug !== null (slug приходит событиями сокета) */}
      <ProfileLink
        slug={slug}
        className={`
          shrink-0 w-9 h-9 rounded-full overflow-hidden bg-dark-secondary flex items-center justify-center
          border-2 ${isMe ? 'border-accent-primary/60' : 'border-white/15'}
        `}
      >
        {ava ? (
          <img src={ava} alt={name || ''} className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <User className="w-4 h-4 text-text-secondary" />
        )}
      </ProfileLink>

      {/* Имя */}
      <div className="flex-1 min-w-0">
        {/* Бейдж банды приходит теми же событиями сокета, что и slug, и стоит
            отдельной строкой над ником: строка игрока узкая, «[Банда] Ник»
            одной строкой обрезается. Обёртка — flex: у инлайнового бейджа
            появляется строчный strut, из-за него строки съезжают вниз */}
        {team?.name && (
          <div className="flex min-w-0 leading-tight">
            <TeamTag team={team} className="mr-0 min-w-0 text-[11px] leading-tight" />
          </div>
        )}
        <p className="truncate text-sm font-semibold leading-tight">
          <ProfileLink slug={slug}>{name || '—'}</ProfileLink>
        </p>
      </div>

      {/* Множитель вывода / выигрыш / сумма ставки */}
      <div className="shrink-0 text-right space-y-0.5">
        {cashed ? (
          // Выигрыш и множитель — в одну строку: сумма, справа бейдж икса
          <div className="flex items-center justify-end gap-1.5 tabular-nums">
            <span className="inline-flex items-center gap-1 text-sm font-bold">
              <Gem className="w-3.5 h-3.5 text-accent-primary" />
              {Number(win || 0).toLocaleString('ru-RU')}
            </span>
            <span className="px-1.5 py-0.5 rounded-md bg-accent-primary/15 border border-accent-primary/30 text-xs font-bold text-accent-primary">
              {hundredsToX(cashoutMultiplier)}×
            </span>
          </div>
        ) : (
          <div className="inline-flex items-center gap-1 text-sm font-bold tabular-nums">
            <Gem className="w-3.5 h-3.5 text-accent-primary" />
            {Number(amount || 0).toLocaleString('ru-RU')}
          </div>
        )}
      </div>

      {/* Выигранный скин (если был) */}
      {winSkin && (
        <div
          className="shrink-0 w-9 h-9 rounded-md overflow-hidden border-2 bg-dark-secondary shadow-[0_0_12px_-2px_rgba(0,255,127,0.4)]"
          style={{ borderColor: winSkin.color || '#444' }}
          title={`${winSkin.name} · ${winSkin.cost}`}
        >
          {winSkin.img ? (
            <img src={winSkin.img} alt={winSkin.name || ''} className="w-full h-full object-cover" loading="lazy" />
          ) : null}
        </div>
      )}
    </div>
  )
}

export default CrashPlayersList
